import { useState } from 'react'
import { useRouter } from 'next/router'
import Template from '@app/components/template'
import { translate } from '@app/locale/translate'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs';

import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { createAccount } from '@app/store'

import Modal from '@app/components/modal'
import Alert, { getNetworkError } from '@app/components/alert'
import { EMAIL_REGEX, POSTAL_REGEX, PHONE_REGEX } from '@app/components/utils'
import ReactHtmlParser from 'react-html-parser'
import { Flex, Box, Button, Text, Heading } from 'rebass'
import {
  Label,
  Input,
  Checkbox
} from '@rebass/forms'

const Create = ({ city, createAccount }) => {
  const router = useRouter()

  const { register, errors, handleSubmit, watch } = useForm()

  const [errorMessage, setErrorMessage] = useState({})
  const [alertMessage, setAlertMessage] = useState({})

  const onSubmit = data => {
    createAccount({
      email: data.email,
      password: data.password,
      first_name: data.firstname,
      last_name: data.lastname,
      address: data.address,
      zip_code: data.postal,
      phone: data.phone,
      locale: dayjs.locale(),
    },
      city.slug,
      () => {
        router.push('/welcome')
      },
      (err) => {
        // console.log('failed to create', err)
        if (err.status === 409) {
          setAlertMessage({
            title: translate('Error'),
            message: translate('An account with this email address already exists. Click <a href="/account/login">here</a> to login'),
            actions: [{
              title: translate('Dismiss'),
              class: 'primary',
              action: () => { setAlertMessage({}) }
            }]
          })
        }
        else if (err.status === 403) {
          setAlertMessage({
            title: translate('Error'),
            message: translate("OOPS! It looks like registration hasn't opened yet for this city. Please contact your city organizer or check back later."),
            actions: [{
              title: translate('Dismiss'),
              class: 'primary',
              action: () => { setAlertMessage({}) }
            }]
          })
        }
        else {
          setErrorMessage(getNetworkError(err))
        }
      })
  }

  return (
    <Template>
      <Flex
        as='form'
        onSubmit={handleSubmit(onSubmit)}
        width={1}
        mx='auto'
        flexWrap='wrap'
        sx={{
          maxWidth: 720,
        }}
      >
        <Heading
          px={[1, 2, 2]}
          my={[2, 3, 4]}
          fontSize={[2, 3, 4]}>
          {translate('Sign Up for')} {city.festival_name}
        </Heading>
        <Box
          my={2}
          px={[1, 2, 2]}
          width={1}
        >
          <Label mb={2} htmlFor='email'>{translate('Email Address')} *</Label>
          <Input
            id='email'
            name='email'
            placeholder='yourname@gmail.com'
            ref={register({
              required: true,
              pattern: EMAIL_REGEX
            })}
          />
          <Text fontSize={1} color='accent'>{errors.email && translate("Invalid email address")}</Text>
        </Box>

        <Box
          width={[1, 1 / 2, 1 / 2]}
          my={2}
          px={2}
        >
          <Label mb={2} htmlFor='password'>{translate('Password (at least 8 characters)')} *</Label>
          <Input
            id='password'
            name='password'
            type='password'
            ref={register({
              required: true,
              minLength: 8
            })}
          />
          <Text fontSize={1} color='accent'>{errors.password && translate("password must be at least 8 characters")}</Text>
        </Box>
        <Box
          width={[1, 1 / 2, 1 / 2]}
          my={2}
          px={2}
        >
          <Label mb={2} htmlFor='confirm'>{translate('Confirm Password')} *</Label>
          <Input
            id='confirmpassword'
            name='confirmpassword'
            type='password'
            ref={register({
              required: true,
              validate: value => value === watch('password')
            })}
          />
          <Text fontSize={1} color='accent'>{errors.confirmpassword && "does not match the password"}</Text>
        </Box>

        <Box
          width={[1, 1 / 2, 1 / 2]}
          my={2}
          px={2}
        >
          <Label mb={2} htmlFor='firstname'>{translate('First Name')} *</Label>
          <Input
            id='firstname'
            name='firstname'
            type='firstname'
            ref={register({
              required: true
            })}
          />
          <Text fontSize={1} color='accent'>{errors.firstname && translate("please enter a valid name")}</Text>
        </Box>
        <Box
          width={[1, 1 / 2, 1 / 2]}
          my={2}
          px={2}
        >
          <Label mb={2} htmlFor='lastname'>{translate('Last Name')} *</Label>
          <Input
            id='lastname'
            name='lastname'
            type='lastname'
            ref={register({
              required: true
            })}
          />
          <Text fontSize={1} color='accent'>{errors.lastname && translate("please enter a valid name")}</Text>
        </Box>

        <Box
          width={[1, 1 / 2, 1 / 2]}
          my={2}
          px={2}
        >
          <Label mb={2} htmlFor='address'>{translate('Street Address')} *</Label>
          <Input
            id='address'
            name='address'
            type='address'
            ref={register({
              required: true
            })}
          />
          <Text fontSize={1} color='accent'>{errors.address && translate("please enter a street address")}</Text>
        </Box>
        <Box
          width={[1, 1 / 4, 1 / 4]}
          my={2}
          px={2}
        >
          <Label mb={2} htmlFor='postal'>{translate('Postal Code')} *</Label>
          <Input
            id='postal'
            name='postal'
            type='postal'
            ref={register({
              required: true,
              // pattern: POSTAL_REGEX
            })}
          />
          <Text fontSize={1} color='accent'>{errors.postal && translate("please enter a valid postal code")}</Text>
        </Box>
        <Box
          width={[1, 1 / 4, 1 / 4]}
          my={2}
          px={2}
        >
          <Label mb={2} htmlFor='phone'>{translate('Phone Number')} *</Label>
          <Input
            id='phone'
            name='phone'
            type='phone'
            ref={register({
              required: true,
              // pattern: PHONE_REGEX
            })}
          />
          <Text fontSize={1} color='accent'>{errors.phone && translate("please enter a valid phone number")}</Text>
        </Box>

        <Box
          width={1}
          my={2}
          px={2}
        >
          <Label mb={2} htmlFor='terms'>{translate('Terms & Conditions')}</Label>
          <Box
            id='terms'
            name='terms'
            p={16}
            height={160}
            sx={{
              overflowY: 'scroll',
              border: '1px solid black'
            }}
          >
            <Text>{ReactHtmlParser(city.terms)}
            </Text>
          </Box>
        </Box>
        <Box
          width={1}
          px={2}
        >
          <Label lineHeight={1.5}>
            <Checkbox
              id='complete'
              name='complete'
              ref={register({
                required: true
              })}
            />
            {translate('I have read and agree to the terms and conditions')}
          </Label>
          <Text fontSize={1} color='accent'>{errors.complete && translate('please read and agree with terms & conditions')}</Text>
        </Box>
        <Box
          width={1}
          px={2}
        >
          <Button mt={16} type="submit">
            {translate('Sign Up')}
          </Button>
        </Box>
      </Flex>

      <Alert
        open={errorMessage.title !== undefined}
        title={errorMessage.title}
        message={errorMessage.message}
        onClose={() => { setErrorMessage({}) }}
      ></Alert>
      <Modal
        open={alertMessage.title !== undefined}
        title={alertMessage.title}
        message={alertMessage.message}
        actions={alertMessage.actions}
        onClose={alertMessage.onClose ? alertMessage.onClose : () => {
          setAlertMessage({})
        }}
      ></Modal>
    </Template>
  )
}

const mapStateToProps = state => ({
  city: state.city
})

const mapDispatchToProps = dispatch => ({
  createAccount: bindActionCreators(createAccount, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(Create)
